import {isString} from 'lodash';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {hasProperty} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {usePushEvents} from '../../../../srcCommon/push/pushContext';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {ChannelTypesEnum, isChannelType} from '../../../models/api/channelCommonTypes';
import {ChannelModel} from '../../../models/api/channelModel';
import {ExternalChannelTypeAuthorizationTypesEnum} from '../../../models/api/externalChannelTypeModel';
import {InboxModel} from '../../../models/api/inboxModel';
import {SettingsUrlModel} from '../../../models/url/settingsUrlModel';
import {openElectronAuthPopup} from '../../../services/electron/electronBridgeOutbound';
import {openExternalPopupUrl} from '../../../services/popups';
import {getVisualSettings, InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {getHost} from '../../../store/data/dataSelectors';
import {getFlatExternalChannelTypes} from '../../../store/data/externalChannelTypes/externalChannelTypesSelectors';
import {getUser} from '../../../store/data/teammates/teammatesSelectors';

export function useReauthorizeChannel(channel: ChannelModel) {
  const host = useAppSelector(getHost);
  const externalChannelTypes = useAppSelector(getFlatExternalChannelTypes);
  const externalChannelType = externalChannelTypes.find((e) => e.id === channel.externalChannelTypeId);

  return () => {
    if (channel.type === ChannelTypesEnum.GMAIL) {
      const hint = makeHint(channel.id, channel.address);
      openAuthUrl(`${host}/accounts/oauth/gmail${hint ? `?address=${encodeURIComponent(hint)}` : ''}`);
    } else if (channel.type === ChannelTypesEnum.OFFICE365) {
      const hint = makeHint(channel.id, channel.address);
      openAuthUrl(`${host}/accounts/oauth/office365?state_data=${encodeURIComponent(hint)}`);
    } else if (channel.type === ChannelTypesEnum.FACEBOOK) {
      const hint = makeHint(channel.id);
      openAuthUrl(`${host}/accounts/oauth/facebook?state_data=${encodeURIComponent(hint)}`);
    } else if (channel.type === ChannelTypesEnum.INTERCOM) {
      openAuthUrl(`${host}/accounts/oauth/intercom`);
    } else if (channel.type === ChannelTypesEnum.TWITTER) {
      const hint = makeHint(channel.id);
      openAuthUrl(`${host}/accounts/oauth/twitter?state_data=${encodeURIComponent(hint)}`);
    } else if (channel.type === ChannelTypesEnum.TWITTER_DM) {
      const hint = makeHint(channel.id);
      openAuthUrl(`${host}/accounts/oauth/twitter_dm?state_data=${encodeURIComponent(hint)}`);
    }

    if (
      externalChannelType &&
      externalChannelType.authorizationType === ExternalChannelTypeAuthorizationTypesEnum.OAUTH
    ) {
      const hint = makeHint(channel.id);
      openAuthUrl(
        `${host}/accounts/oauth/external/${encodeURIComponent(
          channel.externalChannelTypeId || '',
        )}?state_data=${encodeURIComponent(hint)}`,
      );
    }
  };
}

function openAuthUrl(url: string) {
  if (openElectronAuthPopup(url)) {
    return;
  }

  openExternalPopupUrl(url);
}

function makeHint(channelId: number, channelAddress?: string) {
  return JSON.stringify({
    id: channelId,
    address: channelAddress,
  });
}

export function useAccountSavedEventForChannel(
  channel: ChannelModel,
  handler: HandlerOf<{type: ChannelTypesEnum; address: string}>,
) {
  usePushEvents(
    (name, account) => {
      if (name !== 'account_saved' && name !== 'channel_credentials_saved') {
        return;
      }

      if (
        hasProperty(account, 'type') &&
        isChannelType(account.type) &&
        hasProperty(account, 'address') &&
        isString(account.address)
      ) {
        handler({type: account.type, address: account.address});
      }
    },
    [channel],
  );
}

export function useReportChannelCreationEvent(url: SettingsUrlModel) {
  const user = useAppSelector(getUser);

  return (eventType: InterfaceEventsEnum, channel: ChannelModel, inbox: InboxModel, overrides: object) => {
    trackInterfaceEvent(eventType, {
      ...overrides,
      namespace: hasProperty(url, 'namespace') ? url.namespace : '',
      triggerOAuthType: channel.type,
      inbox: {
        id: inbox.id,
        numTeammates: inbox.teammateIds?.length,
        isPublic: inbox.isPublic,
        isAutojoinOn: inbox.isAutojoinOn,
      },
      visualSettings: getVisualSettings(url, user),
      // TODO: replace that when building creation flow
      newFlow: false,
      channel: {
        id: channel.id,
        type: channel.type,
        provider: hasProperty(channel.preferences, 'provider') && channel.preferences.provider,
        isImportHistoryOn: channel.preferences.importHistory,
        isSyncTagsOn: channel.preferences.syncTags,
      },
    });
  };
}

export function useReportInboxCreationEvent(url: SettingsUrlModel) {
  const user = useAppSelector(getUser);

  return (eventType: InterfaceEventsEnum, inbox: InboxModel, overrides: object) => {
    trackInterfaceEvent(eventType, {
      ...overrides,
      inbox: {
        id: inbox.id,
        numTeammates: inbox.teammateIds?.length,
        isPublic: inbox.isPublic,
        isAutojoinOn: inbox.isAutojoinOn,
      },
      visualSettings: getVisualSettings(url, user),
      newFlow: true,
    });
  };
}

export function useChannelEditSettingsEvent() {
  return (eventType: InterfaceEventsEnum, inbox: InboxModel, channel: ChannelModel) => {
    trackInterfaceEvent(eventType, {
      inbox: {
        id: inbox.id,
        numTeammates: inbox.teammateIds?.length,
        isPublic: inbox.isPublic,
        isAutojoinOn: inbox.isAutojoinOn,
      },
      channel: {
        id: channel.id,
        type: channel.type,
        provider: hasProperty(channel.preferences, 'provider') && channel.preferences.provider,
        isImportHistoryOn: channel.preferences.importHistory,
        isSyncTagsOn: channel.preferences.syncTags,
      },
    });
  };
}
